import { HttpClient, HttpErrorResponse } from '@angular/common/http';
import { Injectable, OnDestroy } from '@angular/core';
import {
  BehaviorSubject,
  EMPTY,
  Observable,
  Subject,
  catchError,
  combineLatest,
  defer,
  filter,
  map,
  of,
  repeat,
  retry,
  shareReplay,
  startWith,
  switchMap,
  take,
  takeUntil,
  tap,
  throwError,
  timer,
} from 'rxjs';
import moment from 'moment';
import { environment } from '../../../environments/environment';
import { generateRequestTimeLogHeader } from '../interceptors/log.interceptor';
import { CompanyService } from './company.service';
import { UserService } from './user.service';
import { FeatureToggleService } from './feature-toggle.service';
import { UserInactivityService } from './user-inactivity.service';
import { LoggerService } from './logger.service';
import { LogEvent } from '../enums/log-event';
import { FeatureToggles } from '../enums/feature-toggles.enum';
import { StartService } from './start.service';
import { compareCreditsV2, creditsV2Level } from '../utils/credits-v2.utils';

/** Per-company node of the `GET /credits/balance-report` response. */
export interface CompanyCreditsReport {
  total: number;
  used: number;
  percentageUsed: number;
  percentageRemaining: number;
  nextRenewalDate: string | null;
  runOutDate: string | null;
  /** Real balance; sent by the BFF since TAS-600 (absent on older deploys). */
  totalRemaining?: number;
  /** Meta free service messages (in sends); null when the company has none. */
  bonusWallet?: { balance: number; totalAmount: number; renewalDate: string } | null;
}

interface BalanceReportResponse {
  creditsReport: Record<string, CompanyCreditsReport>;
}

export type SeverityClass = 'is-success' | 'is-warning' | 'is-danger';

export interface CompanyBalance {
  /** Unique key of the row; `name` can repeat across companies. */
  companyId: string;
  name: string;
  used: number;
  usedWidth: string;
  usedCountLabel: string;
  totalLabel: string;
  remainingLabel: string;
  /** 0 = healthy, 1 = half used, 2 = critical, 3 = empty. */
  level: number;
  /** Theme-token driven class applied to the row for coloring. */
  severityClass: SeverityClass;
  warn: boolean;
  full: boolean;
  /** Has Meta free service messages left. */
  bonus: boolean;
  /**
   * Inside the Meta bonus with credits left: they are not being consumed (v2 card
   * greys out while usage is normal). An empty company with bonus is not frozen.
   */
  frozen: boolean;
  /** Formatted `nextRenewalDate`; undefined when the API sends no valid renewal date. */
  renewDate?: string;
  /** Formatted `runOutDate`; undefined when the company has no run-out date. */
  runOutDate?: string;
}

export type CreditsVariation = 'clear' | 'warning' | 'danger';

export interface CreditsSummary {
  /** Highest severity level across all companies. */
  worstLevel: number;
  /** Number of companies at warning or danger. */
  alertCount: number;
  /** Button/banner style driven by the worst company. */
  variation: CreditsVariation;
}

export type CreditsAlertMessageKey =
  | 'ALERT_ONE_LOW'
  | 'ALERT_MANY_LOW'
  | 'ALERT_ONE_EMPTY'
  | 'ALERT_MANY_EMPTY'
  // v2 copy (Figma "Alert Cobrança")
  | 'ALERT_ONE_CRITICAL'
  | 'ALERT_MANY_CRITICAL'
  | 'ALERT_ONE_BLOCKED'
  | 'ALERT_MANY_BLOCKED'
  | 'ALERT_ONE_BLOCKED_BONUS'
  | 'ALERT_MANY_BLOCKED_BONUS';

/** v1 uses ONE/MANY; the v2 banner always reads "Ver créditos". */
export type CreditsAlertCtaKey = 'ALERT_CTA_ONE' | 'ALERT_CTA_MANY' | 'ALERT_CTA_VIEW';

export interface CreditsAlert {
  /** Whether the top-bar alert banner should be shown at all. */
  show: boolean;
  /** Banner style driven by the worst at-risk company. */
  variation: CreditsVariation;
  /** Highest severity level among at-risk companies (drives the pulse). */
  worstLevel: number;
  /** Number of companies at warning or danger. */
  count: number;
  /** i18n key (under CREDITS_BALANCE) for the banner message. */
  messageKey: CreditsAlertMessageKey;
  /** Company name interpolated into the single-company messages. */
  companyName?: string;
  /** i18n key (under CREDITS_BALANCE) for the call-to-action. */
  ctaKey: CreditsAlertCtaKey;
  /** `enable_whatsapp_credits_v2`: Figma banner (buy button, "Ver créditos" link). */
  v2?: boolean;
  /** v2: some company ran out of credits — solid red banner. */
  blocked?: boolean;
}

const POLL_INTERVAL_MS = 10 * 60 * 1000;
const ACCOUNT_TYPE = 'whatsapp';
/** Retries on a 401, giving the auth interceptor time to refresh the token. */
const UNAUTHORIZED_RETRY_COUNT = 2;
const UNAUTHORIZED_RETRY_DELAY_MS = 1500;

/** Neutral summary shown by the button before the first report resolves. */
const CLEAR_SUMMARY: CreditsSummary = {
  worstLevel: 0,
  alertCount: 0,
  variation: 'clear',
};

@Injectable({
  providedIn: 'root',
})
export class CreditsBalanceService implements OnDestroy {
  private readonly destroy$ = new Subject<void>();

  /**
   * Polled `GET /credits/balance-report` response, keyed by companyId. Only fetched
   * for non-global users that have the `livechat_enable_credits_report_modal` toggle
   * and at least one company with WhatsApp credits (Gupshup + number, same rule as the dashboard); otherwise empty so the button/modal/alert
   * stay hidden. Refreshed every 10 min (held while the user is idle); errors keep the last emitted report.
   *
   * The global-user gate lives in `pollReport` (not here) because the credits button
   * can subscribe before the session user is loaded, when a defer-time check would
   * read undefined and wrongly fall through.
   *
   * Waits for `sessionReady$`: the alert banner lives in app.component and
   * subscribes before `/start/livechat` resolves, reading stale companies from
   * localStorage. A user that still has to change the password gets
   * `invalid_token_refresh` on start (recovery flow), but an early credits call
   * returns `invalid_token`, which sends them to the admin instead. A failed
   * session never emits here, so the request is never made.
   */
  private readonly report$: Observable<Record<string, CompanyCreditsReport>> =
    this.whenSessionReady(() =>
      this.featureToggleService.someCompanyCanAccess(
        FeatureToggles.LIVECHAT_ENABLE_CREDITS_REPORT_MODAL
      )
    ).pipe(
      switchMap((canAccess) =>
        canAccess
          ? this.pollReport()
          : of<Record<string, CompanyCreditsReport>>({})
      ),
      shareReplay({ bufferSize: 1, refCount: false })
    );

  /**
   * Button-visibility gate resolved from toggle + companies + user WITHOUT waiting
   * for the balance HTTP. Mirrors the `pollReport` gate (has the modal toggle,
   * non-global, at least one company with WhatsApp credits (Gupshup + number, same rule as the dashboard)) so the button appears as soon
   * as the user is eligible instead of popping in after the first fetch completes.
   */
  public readonly eligible$: Observable<boolean> = this.whenSessionReady(() =>
    this.featureToggleService.someCompanyCanAccess(
      FeatureToggles.LIVECHAT_ENABLE_CREDITS_REPORT_MODAL
    )
  ).pipe(
    map(
      (canAccess) =>
        canAccess &&
        !this.userService.isCurrentUserGlobal() &&
        (this.companyService.getCompanies() ?? []).some(
          (company) => company.hasWhatsappCredits
        )
    ),
    shareReplay({ bufferSize: 1, refCount: false })
  );

  /** `enable_whatsapp_credits_v2`: new modal, severity rules and ordering. */
  public readonly v2Enabled$: Observable<boolean> = defer(() =>
    this.featureToggleService.someCompanyCanAccess(
      FeatureToggles.ENABLE_WHATSAPP_CREDITS_V2
    )
  ).pipe(shareReplay({ bufferSize: 1, refCount: false }));

  private readonly lastUpdatedAtSubject = new BehaviorSubject<Date | null>(null);

  /** Time of the last successful balance report fetch (v2 "Última atualização"). */
  public readonly lastUpdatedAt$ = this.lastUpdatedAtSubject.asObservable();

  /** True until the first balance report resolves; drives the modal spinner. */
  public readonly loading$: Observable<boolean> = this.report$.pipe(
    map(() => false),
    startWith(true)
  );

  /** Per-company credit usage view model. */
  public readonly balances$: Observable<CompanyBalance[]> = combineLatest([
    this.report$,
    this.v2Enabled$,
  ]).pipe(
    map(([report, v2]) => {
      const balances = Object.entries(report)
        // Companies with no credits (no account / deactivated) come back zeroed; hide them.
        .filter(([, company]) => company.total > 0)
        .map(([companyId, company]) => this.buildBalance(companyId, company, v2));

      return v2 ? balances.sort(compareCreditsV2) : balances;
    })
  );

  /** Aggregated severity used by the top-bar button (and future alert banner). */
  public readonly summary$: Observable<CreditsSummary> = this.balances$.pipe(
    map((balances) => this.buildSummary(balances)),
    startWith(CLEAR_SUMMARY)
  );

  /** View model consumed by the top-bar alert banner. */
  public readonly alert$: Observable<CreditsAlert> = combineLatest([
    this.balances$,
    this.v2Enabled$,
  ]).pipe(map(([balances, v2]) => this.buildAlert(balances, v2)));

  constructor(
    private httpClient: HttpClient,
    private companyService: CompanyService,
    private userService: UserService,
    private featureToggleService: FeatureToggleService,
    private userInactivityService: UserInactivityService,
    private loggerService: LoggerService,
    private startService: StartService,
  ) {}

  ngOnDestroy(): void {
    this.destroy$.next();
    this.destroy$.complete();
  }

  /** Defers `source` until the live-chat session (user + companies) is loaded. */
  private whenSessionReady<T>(source: () => Observable<T>): Observable<T> {
    return defer(() => this.startService.sessionReady$).pipe(
      take(1),
      switchMap(source)
    );
  }

  /**
   * 10 min poll of the balance report for the user's companies with WhatsApp credits.
   * The interval keeps running while the user is idle, but a tick that lands while
   * idle holds the request (pausing the poll) until the user is back, then fetches
   * right away and restarts the interval.
   */
  private pollReport(): Observable<Record<string, CompanyCreditsReport>> {
    // Checked here, at emission time, rather than at subscribe: the session user
    // is reliably loaded by now, so global users never trigger the request.
    if (this.userService.isCurrentUserGlobal()) {
      return of<Record<string, CompanyCreditsReport>>({});
    }

    const companiesIds = (this.companyService.getCompanies() ?? [])
      .filter((company) => company.hasWhatsappCredits)
      .map((company) => company.companyId);

    if (!companiesIds.length) {
      return of<Record<string, CompanyCreditsReport>>({});
    }

    return this.waitUntilActive().pipe(
      switchMap(() =>
        this.fetchReport(companiesIds).pipe(
          catchError(() => EMPTY)
        )
      ),
      repeat({ delay: POLL_INTERVAL_MS }),
      takeUntil(this.destroy$)
    );
  }

  /**
   * Emits once the user is active: right away if they already are, otherwise the
   * poll is held (logged) until they're back. Deferred so each poll cycle tracks
   * its own hold.
   */
  private waitUntilActive(): Observable<boolean> {
    return defer(() => {
      let heldAt: number | null = null;

      // idle$ replays the current state, kept accurate by AppComponent's permanent
      // subscription (refCount would otherwise reset it to active on every cycle).
      return this.userInactivityService.idle$.pipe(
        tap((idle) => {
          if (idle) {
            heldAt = Date.now();
            this.loggerService.log(LogEvent.LIVECHAT_CREDITS_REPORT_POLL_HELD);
          }
        }),
        filter((idle) => !idle),
        take(1),
        tap(() => {
          if (heldAt !== null) {
            this.loggerService.log(LogEvent.LIVECHAT_CREDITS_REPORT_POLL_RESUMED, {
              heldForMs: Date.now() - heldAt,
            });
          }
        })
      );
    });
  }

  public fetchReport(
    companiesIds: string[]
  ): Observable<Record<string, CompanyCreditsReport>> {
    return this.httpClient
      .get<BalanceReportResponse>(
        `${environment.externalUrls.bff}/credits/balance-report`,
        {
          params: {
            companiesIds: companiesIds.join(','),
            accountType: ACCOUNT_TYPE,
            timeZone: moment().format('Z'),
          },
          headers: generateRequestTimeLogHeader('get-credits-balance-report'),
        }
      )
      .pipe(
        map((response) => response.creditsReport ?? {}),
        tap(() => this.lastUpdatedAtSubject.next(new Date())),
        // The token lives in an httpOnly cookie refreshed out of band (by the auth
        // interceptor on 401, or the 30-min scheduler). When the poll fires with an
        // expired token it gets a 401; retry after a short delay so the follow-up
        // request carries the refreshed cookie instead of failing until the next tick.
        retry({
          count: UNAUTHORIZED_RETRY_COUNT,
          delay: (error: HttpErrorResponse) =>
            error?.status === 401
              ? timer(UNAUTHORIZED_RETRY_DELAY_MS)
              : throwError(() => error),
        })
      );
  }

  private buildBalance(
    companyId: string,
    company: CompanyCreditsReport,
    v2 = false
  ): CompanyBalance {
    const remaining =
      company.totalRemaining ?? Math.max(company.total - company.used, 0);
    const level = v2
      ? creditsV2Level({
          total: company.total,
          percentageUsed: company.percentageUsed,
          totalRemaining: remaining,
        })
      : this.severityLevel(company.percentageRemaining);
    const name =
      this.companyService
        .getCompanies()
        ?.find((c) => c.companyId === companyId)?.companyName ?? companyId;

    // An empty company (below one credit in v2) reads as fully used, even if the
    // API still reports a fraction of a credit left.
    const empty = level === 3;
    const bonus = (company.bonusWallet?.balance ?? 0) > 0;

    return {
      companyId,
      name,
      // Floor, not round: 99.55% must not read as "100% usados" while credits remain.
      used: empty ? 100 : Math.floor(company.percentageUsed),
      usedWidth: empty ? '100%' : `${company.percentageUsed}%`,
      usedCountLabel: Math.round(company.used).toLocaleString('pt-BR'),
      totalLabel: company.total.toLocaleString('pt-BR'),
      remainingLabel: empty ? '0' : Math.round(remaining).toLocaleString('pt-BR'),
      level,
      severityClass: this.severityClass(level),
      warn: level > 0,
      full: company.percentageRemaining >= 100,
      bonus,
      frozen: bonus && !empty,
      renewDate: this.formatDate(company.nextRenewalDate),
      runOutDate: this.formatDate(company.runOutDate),
    };
  }

  /** Formats an API date, or undefined when it's missing/unparseable (avoids "Invalid date"). */
  private formatDate(date: string | null): string | undefined {
    if (!date) {
      return undefined;
    }

    const parsed = moment(date);
    return parsed.isValid() ? parsed.format('DD/MM/YYYY') : undefined;
  }

  private buildSummary(balances: CompanyBalance[]): CreditsSummary {
    const worstLevel = balances.reduce((worst, balance) => Math.max(worst, balance.level), 0);
    const alertCount = balances.filter((balance) => balance.level > 0).length;
    const variation: CreditsVariation =
      worstLevel === 0 ? 'clear' : worstLevel === 1 ? 'warning' : 'danger';

    return { worstLevel, alertCount, variation };
  }

  /** Empty companies win over merely-low ones; single vs. multiple picks the copy. */
  private buildAlert(balances: CompanyBalance[], v2 = false): CreditsAlert {
    const atRisk = balances
      .filter((balance) => balance.level > 0)
      .sort((a, b) => b.level - a.level || b.used - a.used);
    const depleted = atRisk.filter((balance) => balance.level === 3);
    const worst = atRisk[0];

    if (!worst) {
      return {
        show: false,
        variation: 'clear',
        worstLevel: 0,
        count: 0,
        messageKey: 'ALERT_ONE_LOW',
        ctaKey: 'ALERT_CTA_ONE',
      };
    }

    if (v2 && depleted.length) {
      return this.buildBlockedAlertV2(depleted, atRisk.length);
    }

    if (v2 && worst.level === 2) {
      return this.buildCriticalAlertV2(atRisk.filter((balance) => balance.level === 2));
    }

    const many = depleted.length > 1 || atRisk.length > 1;
    let messageKey: CreditsAlertMessageKey;
    let companyName: string | undefined;

    if (depleted.length > 1) {
      messageKey = 'ALERT_MANY_EMPTY';
    } else if (depleted.length === 1) {
      messageKey = 'ALERT_ONE_EMPTY';
      companyName = depleted[0].name;
    } else if (atRisk.length > 1) {
      messageKey = 'ALERT_MANY_LOW';
    } else {
      messageKey = 'ALERT_ONE_LOW';
      companyName = worst.name;
    }

    return {
      show: true,
      variation: worst.level === 1 ? 'warning' : 'danger',
      worstLevel: worst.level,
      count: atRisk.length,
      messageKey,
      companyName,
      ctaKey: v2 ? 'ALERT_CTA_VIEW' : many ? 'ALERT_CTA_MANY' : 'ALERT_CTA_ONE',
      v2,
    };
  }

  /**
   * v2: some company ran out — solid red banner. "Foi bloqueado" as soon as one empty
   * company has no Meta bonus; only when all of them still have bonus does the copy
   * say sending stops once the bonus ends.
   */
  private buildBlockedAlertV2(depleted: CompanyBalance[], atRiskCount: number): CreditsAlert {
    const one = depleted.length === 1;
    const anyWithoutBonus = depleted.some((balance) => !balance.bonus);
    let messageKey: CreditsAlertMessageKey;

    if (anyWithoutBonus) {
      messageKey = one ? 'ALERT_ONE_BLOCKED' : 'ALERT_MANY_BLOCKED';
    } else {
      messageKey = one ? 'ALERT_ONE_BLOCKED_BONUS' : 'ALERT_MANY_BLOCKED_BONUS';
    }

    return {
      show: true,
      variation: 'danger',
      worstLevel: 3,
      count: atRiskCount,
      messageKey,
      companyName: one ? depleted[0].name : undefined,
      ctaKey: 'ALERT_CTA_VIEW',
      v2: true,
      blocked: true,
    };
  }

  /** v2: 90%+ used somewhere (nobody empty) — light red "estão acabando" banner. */
  private buildCriticalAlertV2(critical: CompanyBalance[]): CreditsAlert {
    const one = critical.length === 1;

    return {
      show: true,
      variation: 'danger',
      worstLevel: 2,
      count: critical.length,
      messageKey: one ? 'ALERT_ONE_CRITICAL' : 'ALERT_MANY_CRITICAL',
      companyName: one ? critical[0].name : undefined,
      ctaKey: 'ALERT_CTA_VIEW',
      v2: true,
    };
  }

  /** Escalating severity by remaining %: warning at 75% used, danger at 90% used, empty at 100%. */
  private severityLevel(pct: number): number {
    if (pct <= 0) return 3; // 100% used -> empty
    if (pct <= 10) return 2; // 90%+ used -> danger
    if (pct <= 25) return 1; // 75%+ used -> warning
    return 0; // below 75% used -> healthy
  }

  private severityClass(level: number): SeverityClass {
    if (level === 0) return 'is-success';
    if (level === 1) return 'is-warning';
    return 'is-danger';
  }
}
